/**
 * Dashboard view — F0/F9 overview.
 *
 * Lineage: V1 scaffold (gate phase track) + V3 terminalWidget (manifest)
 * + V4 portalSpa (membrane, Allen & Co).
 *
 * The dashboard is the first surface — it shows the agent's state,
 * the gate's progress, and the register's coverage. Editorial layout.
 * White space first. Data as evidence, not decoration.
 *
 * Terminal-engageable: the status bar at the top shows live agent state
 * with a monospace readout. The user can open the terminal (⌘K) from
 * anywhere to interact.
 */

import { useState, useEffect, useRef } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

interface DashboardViewProps {
  isOperator: boolean;
}

export function DashboardView({ isOperator }: DashboardViewProps) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agentState, setAgentState] = useState<string>("offline");
  const [coverage, setCoverage] = useState(0);
  const [receiptCount, setReceiptCount] = useState(0);
  const [gateState, setGateState] = useState<any>(null);
  const [greenfield, setGreenfield] = useState<any>(null);
  const [latestRun, setLatestRun] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }

    if (isOperator) {
      clients.control.getFleet()
        .then((d) => { setData(applyMembrane(d, false)); })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    } else {
      Promise.all([
        clients.control.getAgentStatus().catch(() => null),
        clients.control.getRegister().catch(() => null),
      ])
        .then(([status, register]) => {
          const s = applyMembrane(status, false) as any;
          const r = applyMembrane(register, false) as any;
          setData({ status: s, register: r });
          setAgentState(s?.state || "offline");
          setCoverage(r?.coverage ?? 0);
          setReceiptCount(r?.receipt_count ?? 0);
        })
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));

      // Load gate state (V1 scaffold lineage)
      clients.control.mcpToolCall("gate.summary", {})
        .then((res: any) => {
          const d = res?.result || res;
          if (d?.gate_id) setGateState(d);
        })
        .catch(() => {});

      // Load greenfield compilation state (F6)
      clients.frontier.listRuns(1)
        .then((res: any) => {
          const runs = res?.runs || res || [];
          if (runs.length > 0) {
            setLatestRun(runs[0]);
            return clients.frontier.getGreenfield(runs[0].run_id || runs[0].id);
          }
          return null;
        })
        .then((res: any) => { if (res) setGreenfield(res); })
        .catch(() => {});

      // Poll agent status every 30s (terminal-engageable: live state)
      pollRef.current = setInterval(() => {
        clients.control.getAgentStatus()
          .then((s: any) => {
            const state = applyMembrane(s, false);
            setAgentState(state?.state || "offline");
          })
          .catch(() => {});
      }, 30000);
    }

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isOperator]);

  if (loading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error-state">{error}</div>;

  if (isOperator) {
    const fleet = data?.tenants || [];
    return (
      <div className="view">
        <div className="view-header">
          <span className="eyebrow">Operator</span>
          <h1>Fleet</h1>
        </div>
        <div className="card-grid">
          {fleet.map((t: any) => (
            <div key={t.id} className="card">
              <div className="card-title">{t.name || t.id}</div>
              <div className="card-meta">
                <span className="badge" data-status={t.agent_status}>{t.agent_status || "unknown"}</span>
                <span>Coverage: {t.coverage_pct ?? 0}%</span>
              </div>
            </div>
          ))}
          {fleet.length === 0 && <div className="empty-state">No tenants yet.</div>}
        </div>
      </div>
    );
  }

  const status = data?.status;
  const register = data?.register;

  return (
    <div className="view">
      {/* Status bar — terminal-inspired live readout */}
      <div className="status-bar">
        <div className="status-bar-left">
          <span className={`status-dot ${agentState}`} />
          <span className="mono status-state">{agentState}</span>
        </div>
        <div className="status-bar-right">
          <span className="mono status-metric">
            <span className="status-metric-val">{coverage}%</span>
            <span className="status-metric-label">coverage</span>
          </span>
          <span className="mono status-metric">
            <span className="status-metric-val">{receiptCount}</span>
            <span className="status-metric-label">receipts</span>
          </span>
          <span className="mono status-hint">⌘K terminal</span>
        </div>
      </div>

      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Inhabited Spec", false)}</span>
        <h1>{applyMembrane("Your Spec", false)}</h1>
        <p className="view-lede">
          {applyMembrane(
            "Your agent continuity, coverage, and the work history that compounds.",
            false
          )}
        </p>
      </div>

      {/* Gate phase track — V1 scaffold lineage */}
      {gateState && gateState.phases && gateState.phases.length > 0 && (
        <div className="gate-phase-track">
          {gateState.phases.map((phase: any, i: number) => (
            <div key={i} className={`gate-phase-step ${phase.status}`}>
              <div className="gate-phase-dot">
                {phase.status === "completed" ? "✓" : phase.status === "skipped" ? "−" : phase.status === "in_progress" ? "●" : phase.key[0].toUpperCase()}
              </div>
              <div className="gate-phase-label">{applyMembrane(phase.name, false)}</div>
              <div className="gate-phase-sublabel">{phase.status.replace("_", " ")}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card-grid">
        <div className="card">
          <div className="card-title">Agent</div>
          <div className="card-meta">
            <span className="badge" data-status={status?.state || "inactive"}>
              {agentState === "executing" ? "executing" : status?.state || "offline"}
            </span>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Coverage</div>
          <div className="coverage-meter">
            <div className="coverage-bar">
              <div className="coverage-fill" style={{ width: `${coverage}%` }} />
            </div>
            <div className="coverage-text">{coverage}%</div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Records</div>
          <div className="card-meta">{receiptCount} receipts</div>
        </div>
      </div>

      {/* F6 Greenfield compilation state */}
      <div className="card">
        <div className="card-title">{applyMembrane("Workspace", false)}</div>
        {greenfield ? (
          <div className="card-meta">
            <span className="badge" data-status={greenfield.status === "compiled" ? "active" : "pending"}>
              {greenfield.status || "pending"}
            </span>
            {greenfield.workspace_id && <span className="mono muted"> {greenfield.workspace_id}</span>}
            {greenfield.last_compiled && <span className="muted"> · {greenfield.last_compiled}</span>}
          </div>
        ) : latestRun ? (
          <div className="card-meta">
            <span className="badge" data-status="pending">{applyMembrane("Pending compilation", false)}</span>
            <span className="muted">run: {latestRun.run_id?.slice(0, 12) || latestRun.id?.slice(0, 12)}…</span>
          </div>
        ) : (
          <p className="muted" style={{ marginTop: 6 }}>
            {applyMembrane("Your workspace is ready to be compiled — start from Work Packet.", false)}
          </p>
        )}
      </div>

      {/* Terminal hint card */}
      <div className="card terminal-hint-card">
        <div className="card-title">{applyMembrane("Command Rail", false)}</div>
        <p className="card-meta" style={{ lineHeight: 1.55, marginTop: 8 }}>
          {applyMembrane("Press ⌘K anywhere to open the terminal. Navigate views, ask your agent, or run commands — all from the keyboard.", false)}
        </p>
        <div className="terminal-hint-commands">
          <span className="mono">/agent</span>
          <span className="mono">/campaigns</span>
          <span className="mono">/offers</span>
          <span className="mono">/inbox</span>
          <span className="mono">/help</span>
        </div>
      </div>
    </div>
  );
}
